<template>
  <div class="chat-header space-between">
    <div class="chat-header__item vertical">
      <span class="chat-header__text">{{ clientName }}</span>

      <div
        class="chat-header__text--faded click"
        @click="toggleInfo"
        v-if="showChannel"
      >
        <div class="chat-header__text-margin">
          <channel-badge
            :type="channelType"
            class="chat-header__text-channel"
          />
        </div>
        <div>{{ channelName }}</div>
      </div>
      <div class="chat-header__text--faded click" @click="toggleInfo" v-else>
        {{ campaignName }}
      </div>
    </div>

    <template v-if="!hideActions">
      <div class="chat-header__item center">
        <span class="chat-header__icon--spaced">
          <clock-icon />
        </span>
        <chat-timer class="chat-header__text" :lastMessage="lastMessageAt" />
      </div>

      <div class="chat-header__item align-right">
        <hcc-input
          v-if="allowSearch"
          :placeholder="$t('chat.search-msg')"
          icon
          size="md"
          :value="inputValue"
        >
          <template #icon>
            <search-icon />
          </template>
        </hcc-input>

        <actions-chat
          v-if="showActionsChat"
          :enableBlock="blockUsersEnabled"
          :enableFavorite="favoritesEnabled"
          :favorite="favorite"
          :blocked="blocked"
          :agentID="agentID"
          :campaignID="campaign.id"
          :channelID="channel.id"
          :client="client"
          :blockFavorite="this.hasFavorite"
        />
      </div>
      <div
        class="chat-header__item-bottom"
        v-if="hasFavorite && favoriteName"
        v-html="$t('chat.has-favorite', { name: favoriteName })"
      ></div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ClockIcon from '@/assets/icons/clock.svg';
import ActionsChat from '@/components/ActionsChat.vue';
import SearchIcon from '@/assets/icons/search.svg';
import HccInput from '@/components/shared/HccInput/index.vue';
import ChatTimer from './ChatTimer.vue';
import ChannelBadge from '@/components/ChannelBadge.vue';
import userByIdGql from '@/graphql/queries/getUserDetail.gql';

export default {
  components: {
    ClockIcon,
    ActionsChat,
    SearchIcon,
    HccInput,
    ChannelBadge,
    ChatTimer,
  },
  props: {
    channel: {
      required: true,
      type: Object,
    },
    client: {
      required: true,
      type: Object,
    },
    lastMessage: {
      required: true,
      type: Object,
    },
    campaign: {
      required: true,
      type: Object,
    },
    favorite: {
      required: true,
      type: Boolean,
    },
    agentID: {
      type: [String, null],
      required: true,
    },
    hideActions: {
      default: false,
      type: Boolean,
    },
    blocked: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      inputValue: '',
      allowSearch: false,
      showChannel: true,
      favoriteName: null,
    };
  },
  computed: {
    ...mapGetters([
      'getConfig',
      'userInfo',
      'activeConversation',
    ]),
    channelName() {
      return this.channel.name ?? '';
    },
    campaignName() {
      return this.campaign.name ?? '';
    },
    clientName() {
      return this.client.fullName || this.client.name || this.client.username || '';
    },
    lastMessageAt() {
      return this.lastMessage.createdAt || '';
    },
    campaignId() {
      return this.campaign.id ?? null;
    },
    channelId() {
      return this.channel.id ?? null;
    },
    configuration() {
      return this.getConfig(this.campaignId, this.channelId);
    },
    blockUsersEnabled() {
      return this.configuration.blockUsersEnabled ?? false;
    },
    favoritesEnabled() {
      return this.configuration.favoritesEnabled ?? false;
    },
    showActionsChat() {
      return this.blockUsersEnabled || this.favoritesEnabled;
    },
    channelType() {
      return this.channel && this.channel.channelType.key ? this.channel.channelType.key : '';
    },
    hasFavorite() {
      if (this.activeConversation && !this.favorite) {
        const { client, conversation } = this.activeConversation;

        return client.favorites.some(({ campaign }) => campaign === conversation.campaign.id);
      }

      return false;
    },
    favoriteAgent() {
      if (this.activeConversation && !this.favorite) {
        const { client, conversation } = this.activeConversation;
        const favorite = client.favorites.find(
          ({ campaign }) => campaign === conversation.campaign.id,
        );
        if (favorite) {
          return favorite.user;
        }
      }
      return null;
    },
  },
  methods: {
    toggleInfo() {
      this.showChannel = !this.showChannel;
    },
    async findUserFavorite() {
      if (this.favoriteAgent) {
        const { data } = await this.$apollo.query({
          query: userByIdGql,
          variables: {
            id: this.favoriteAgent,
          },
        });

        this.favoriteName = data.userById.name;
      } else {
        this.favoriteName = null;
      }
    },
  },
  watch: {
    'activeConversation.client': function clientChange() {
      this.findUserFavorite();
    },
  },
};
</script>
